import React, { useEffect, useState } from 'react';
import BlogCard from './BlogCard';
import { supabase } from '../supabaseClient';
import { calculateReadingTime } from '../utils/blog';

const MAX_VISIBLE_POSTS = 3;
const FETCH_LIMIT = 20;

const BlogPreview = ({ onNavigate }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchPosts = async () => {
      setLoading(true);
      setError(null);

      try {
        const [{ data: postsData, error: postsError }, { data: catsData, error: catsError }] =
          await Promise.all([
            supabase
              .from('blog_posts')
              .select('id, title, slug, excerpt, content, cover_image_url, category_id, published_at')
              .eq('is_published', true)
              .order('published_at', { ascending: false, nullsFirst: false })
              .limit(FETCH_LIMIT),
            supabase
              .from('categories')
              .select('id, name, slug, sort_order')
              .eq('is_active', true)
              .order('sort_order', { ascending: true })
              .order('name', { ascending: true }),
          ]);

        if (postsError) throw postsError;
        if (catsError) throw catsError;

        const catMap = new Map();
        const orderedCats = [];
        (catsData || []).forEach((cat) => {
          if (cat && cat.id != null) {
            catMap.set(cat.id, cat);
            orderedCats.push(cat);
          }
        });

        const normalized = (postsData || []).map((post) => ({
          ...post,
          category: catMap.get(post.category_id) || null,
          readingTime: calculateReadingTime(post.content || post.excerpt || ''),
        }));

        const selected = pickPostsByCategory(normalized, orderedCats);

        if (isMounted) {
          setPosts(selected);
        }
      } catch (err) {
        console.error('Error cargando blog:', err);
        if (isMounted) {
          setError('No pudimos cargar las últimas entradas. Intenta de nuevo más tarde.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchPosts();
    return () => {
      isMounted = false;
    };
  }, []);

  const go = (path) => {
    if (typeof onNavigate === 'function') {
      onNavigate(path);
    } else {
      window.location.assign(path);
    }
  };

  const renderSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {[...Array(3)].map((_, index) => (
        <div
          key={index}
          className="rounded-3xl border border-gray-100 shadow-[0_25px_45px_rgba(15,23,42,0.05)] overflow-hidden bg-white animate-pulse"
        >
          <div className="aspect-[4/3] bg-gray-200" />
          <div className="p-6 space-y-4">
            <div className="h-3 w-24 bg-gray-200 rounded-full" />
            <div className="h-6 w-3/4 bg-gray-200 rounded-full" />
            <div className="h-4 w-full bg-gray-200 rounded-full" />
            <div className="h-4 w-5/6 bg-gray-200 rounded-full" />
            <div className="h-5 w-32 bg-gray-200 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderContent = () => {
    if (loading) return renderSkeleton();
    if (error) {
      return (
        <div className="rounded-2xl border border-red-100 bg-red-50 text-red-700 px-6 py-4">
          {error}
        </div>
      );
    }
    if (!posts.length) {
      return (
        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center text-gray-500">
          Aún no hay entradas publicadas. Muy pronto compartiremos novedades.
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {posts.map((post) => (
          <BlogCard
            key={post.id}
            post={post}
            category={post.category}
            readingTime={post.readingTime}
            onClick={() => go(`/blog/${post.slug}`)}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="bg-gradient-to-b from-white via-[#F8FAFF] to-white py-16 sm:py-20 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <p className="text-sm uppercase tracking-[0.4em] text-brand font-semibold mb-3">
              Blog
            </p>
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full border-2 border-brand px-6 py-2 text-sm font-semibold text-brand hover:bg-brand hover:text-white transition-colors duration-200"
            onClick={() => go('/blog')}
          >
            Ver todas las entradas
          </button>
        </div>

        {renderContent()}
      </div>
    </section>
  );
};

export default BlogPreview;

function pickPostsByCategory(posts, categories) {
  if (!posts?.length) return [];
  const result = [];
  const used = new Set();

  (categories || []).some((cat) => {
    const match = posts.find(
      (post) => post.category_id === cat.id && !used.has(post.id)
    );
    if (match) {
      result.push(match);
      used.add(match.id);
    }
    return result.length === MAX_VISIBLE_POSTS;
  });

  if (result.length < MAX_VISIBLE_POSTS) {
    for (const post of posts) {
      if (result.length === MAX_VISIBLE_POSTS) break;
      if (!used.has(post.id)) {
        result.push(post);
        used.add(post.id);
      }
    }
  }

  return result;
}
